import * as i0 from '@angular/core';
import { EventEmitter, ElementRef, Directive, Inject, Injectable, Renderer2, Input, ViewContainerRef, TemplateRef, HostBinding, Output, HostListener, NgModule } from '@angular/core';
import { Subject, BehaviorSubject } from 'rxjs';
import { registerFontAwesome } from '@fa-switch-sharedservices/web-elements/components/icon/icon-library';
import { setBasePath } from '@fa-switch-sharedservices/web-elements/utilities/base-path';
import { __decorate } from 'tslib';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { I18NextModule } from 'angular-i18next';

class FadvBaseComponent {
    constructor(elementRef) {
        this.elementRef = elementRef;
        this.subscriptions = [];
        this.onInit = new EventEmitter();
        this.afterContentInit = new EventEmitter();
        this.afterViewInit = new EventEmitter();
        this.onChanges = new EventEmitter();
        this.onDestroy = new EventEmitter();
    }
    static get eventStream() {
        return this.dispatcher.asObservable();
    }
    ngOnInit() {
        this.emitEvent(this.onInit);
    }
    ngAfterContentInit() {
        this.emitEvent(this.afterContentInit);
    }
    ngAfterViewInit() {
        this.emitEvent(this.afterViewInit);
    }
    ngOnChanges(changes) {
        var _a;
        if ((_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) {
            Object.keys(changes).forEach((key) => {
                this.elementRef.nativeElement[key] = changes[key].currentValue;
            });
        }
        this.emitEvent(this.onChanges, changes);
    }
    ngOnDestroy() {
        // I am not sure what storybook is doing, but some way it is replacing the references,
        // this doesn't happen in the dev or prod build of the demo app.
        if (this.subscriptions && this.subscriptions.length && this.subscriptions.forEach) {
            this.subscriptions.forEach((s) => s.unsubscribe());
        }
        this.emitEvent(this.onDestroy);
    }
    emitEvent(eventEmitter, value) {
        if (eventEmitter && eventEmitter.emit) {
            eventEmitter.emit(value);
        }
    }
    /**
     * A helper method that hold the subscription reference and unsubscribes onDestroy
     * @param emitter The event emitter to subscribe to
     * @param handler The handler callback that is invoked when an event is emitted
     */
    registerSubscription(emitter, handler) {
        this.subscriptions.push(emitter.subscribe(handler));
    }
    getAttributeValue(value) {
        return value !== null && value !== void 0 ? value : null;
    }
    _forwardCustomEvent(event, emitter, stopPropagation) {
        if (event.target !== this.elementRef.nativeElement)
            return;
        const customEvent = event;
        emitter.emit(customEvent);
        if (stopPropagation) {
            event.stopPropagation();
        }
    }
    reportEvent(eventName) {
        var _a;
        if (!((_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement))
            return;
        if (this.isExcludedEvent(eventName))
            return;
        if ('fadvNoTrack' in this.elementRef.nativeElement.dataset)
            return;
        const page = FadvBaseComponent.findPage();
        const category = FadvBaseComponent.findCategory(this.elementRef.nativeElement);
        setTimeout(() => {
            let event = {
                key: this.elementRef.nativeElement.dataset.fadvKey,
                event: eventName,
                component: this.elementRef.nativeElement.tagName.toLowerCase(),
                metaData: FadvBaseComponent.getMetaData(this.elementRef.nativeElement),
            };
            if (page) {
                event = Object.assign(Object.assign({}, event), { page: page.dataset.fadvPage, metaData: Object.assign(Object.assign({}, event.metaData), FadvBaseComponent.getMetaData(page)) });
            }
            if (category) {
                event = Object.assign(Object.assign({}, event), { category: category.dataset.fadvCategory, metaData: Object.assign(Object.assign({}, event.metaData), FadvBaseComponent.getMetaData(category)) });
            }
            FadvBaseComponent.dispatcher.next(event);
        }, 100);
    }
    isExcludedEvent(eventName) {
        const excluded = this.elementRef.nativeElement.dataset.fadvExclude;
        return !!excluded && excluded.indexOf(eventName) >= 0;
    }
    static findPage() {
        return document.querySelector('[data-fadv-page]');
    }
    static findCategory(el) {
        return el.closest('[data-fadv-category]');
    }
    static getMetaData(element) {
        const ignored = ['fadvKey', 'fadvPage', 'fadvExclude', 'fadvNoTrack', 'fadvCategory'];
        return Object.keys(element.dataset)
            .filter((x) => x.startsWith('fadv') && ignored.indexOf(x) < 0)
            .reduce((acc, x) => {
            let key = x.replace('fadv', '');
            key = `${key[0].toLowerCase()}${key.substring(1)}`;
            const value = element.dataset[x];
            if (acc[key] && value) {
                const existingValue = acc[key];
                acc[key] = `${existingValue}, ${value}`;
            }
            else if (value) {
                acc[key] = value;
            }
            return acc;
        }, {});
    }
}
FadvBaseComponent.dispatcher = new Subject();
FadvBaseComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvBaseComponent, deps: [{ token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FadvBaseComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvBaseComponent, usesOnChanges: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvBaseComponent, decorators: [{
            type: Directive
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Inject,
                        args: [ElementRef]
                    }] }];
    } });

// The public service used for subscribing to events
class FadvComponentsAnalyticsService {
    get events() {
        return FadvBaseComponent.eventStream;
    }
    getPageMetaData() {
        const page = FadvBaseComponent.findPage();
        if (!page)
            return;
        return {
            page: page.dataset.fadvPage,
            metaData: FadvBaseComponent.getMetaData(page),
        };
    }
}
FadvComponentsAnalyticsService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvComponentsAnalyticsService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FadvComponentsAnalyticsService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvComponentsAnalyticsService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvComponentsAnalyticsService, decorators: [{
            type: Injectable
        }] });

class FadvIconLibraryService {
    constructor() {
        this.initialized = false;
    }
    useFontAwesome() {
        registerFontAwesome();
    }
    setBasePath(path) {
        setBasePath(path);
    }
    initialize(basePath = '/assets') {
        if (this.initialized)
            return;
        this.useFontAwesome();
        this.setBasePath(basePath);
        this.initialized = true;
    }
}
FadvIconLibraryService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvIconLibraryService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
FadvIconLibraryService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvIconLibraryService });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvIconLibraryService, decorators: [{
            type: Injectable
        }] });

function Track() {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (target, propertyKey) {
        Object.defineProperty(target, propertyKey, {
            get: function () {
                return this[`${propertyKey.toString()}Base`];
            },
            set: function (value) {
                this[`${propertyKey.toString()}Base`] = value;
                if (this[`${propertyKey.toString()}Subscription`]) {
                    this[`${propertyKey.toString()}Subscription`].unsubscribe();
                }
                this[`${propertyKey.toString()}Subscription`] = this[`${propertyKey.toString()}Base`].subscribe(() => this.reportEvent(propertyKey.toString()));
            },
        });
    };
}

function Forward() {
    return function (target, propertyKey) {
        Object.defineProperty(target, propertyKey, {
            get: function () {
                var _a;
                if (!((_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement))
                    return;
                return this.elementRef.nativeElement[propertyKey];
            },
            set: function (value) {
                var _a;
                if (!((_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement))
                    return;
                this.elementRef.nativeElement[propertyKey] = value;
            },
        });
    };
}

class FadvAppendChildDirective {
    constructor(renderer, elementRef) {
        this.renderer = renderer;
        this.elementRef = elementRef;
    }
    ngAfterViewInit() {
        if (!this.fadvAppendChild)
            return;
        if (!this.elementRef.nativeElement)
            return;
        if ('length' in this.fadvAppendChild) {
            const fragment = new DocumentFragment();
            while (this.fadvAppendChild.length) {
                fragment.appendChild(this.fadvAppendChild[0]);
            }
            this.renderer.appendChild(this.elementRef.nativeElement, fragment);
        }
        else {
            this.renderer.appendChild(this.elementRef.nativeElement, this.fadvAppendChild);
        }
    }
}
FadvAppendChildDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvAppendChildDirective, deps: [{ token: Renderer2 }, { token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FadvAppendChildDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvAppendChildDirective, selector: "[fadvAppendChild]", inputs: { fadvAppendChild: "fadvAppendChild" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvAppendChildDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fadvAppendChild]' }]
        }], ctorParameters: function () {
        return [{ type: i0.Renderer2, decorators: [{
                        type: Inject,
                        args: [Renderer2]
                    }] }, { type: i0.ElementRef, decorators: [{
                        type: Inject,
                        args: [ElementRef]
                    }] }];
    }, propDecorators: { fadvAppendChild: [{
                type: Input
            }] } });

class FadvAutofocusDirective {
    constructor(host) {
        this.host = host;
    }
    ngAfterViewInit() {
        this.host.nativeElement.focus();
    }
}
FadvAutofocusDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvAutofocusDirective, deps: [{ token: ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FadvAutofocusDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvAutofocusDirective, selector: "[autofocus]", ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvAutofocusDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[autofocus]',
                }]
        }], ctorParameters: function () {
        return [{ type: i0.ElementRef, decorators: [{
                        type: Inject,
                        args: [ElementRef]
                    }] }];
    } });

class FadvLetDirective {
    constructor(viewContainer, templateRef) {
        this.viewContainer = viewContainer;
        this.templateRef = templateRef;
        this.context = {};
        this.viewContainer.createEmbeddedView(this.templateRef, this.context);
    }
    set fadvLet(value) {
        this.context.fadvLet = this.context.$implicit = value;
    }
}
FadvLetDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvLetDirective, deps: [{ token: ViewContainerRef }, { token: TemplateRef }], target: i0.ɵɵFactoryTarget.Directive });
FadvLetDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvLetDirective, selector: "[fadvLet]", inputs: { fadvLet: "fadvLet" }, exportAs: [""], ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvLetDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fadvLet]', exportAs: '' }]
        }], ctorParameters: function () {
        return [{ type: i0.ViewContainerRef, decorators: [{
                        type: Inject,
                        args: [ViewContainerRef]
                    }] }, { type: i0.TemplateRef, decorators: [{
                        type: Inject,
                        args: [TemplateRef]
                    }] }];
    }, propDecorators: { fadvLet: [{
                type: Input
            }] } });

class FadvNestedHtmlDirective extends FadvBaseComponent {
    get htmlContent() {
        return this.elementRef.nativeElement.childNodes;
    }
}
FadvNestedHtmlDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvNestedHtmlDirective, deps: null, target: i0.ɵɵFactoryTarget.Directive });
FadvNestedHtmlDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvNestedHtmlDirective, usesInheritance: true, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvNestedHtmlDirective, decorators: [{
            type: Directive
        }] });

class FadvBadgeDirective {
    constructor() {
        this.slotName = 'badge';
    }
}
FadvBadgeDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvBadgeDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvBadgeDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvBadgeDirective, selector: "[fadvBadge]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvBadgeDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fadvBadge]',
                }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

class FadvPrefixDirective {
    constructor() {
        this.slotName = 'prefix';
    }
}
FadvPrefixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvPrefixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvPrefixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvPrefixDirective, selector: "[fadvPrefix]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvPrefixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fadvPrefix]',
                }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

class FadvSuffixDirective {
    constructor() {
        this.slotName = 'suffix';
    }
}
FadvSuffixDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSuffixDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvSuffixDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvSuffixDirective, selector: "[fadvSuffix]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSuffixDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fadvSuffix]',
                }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

class FadvSplitButtonDirective {
    constructor() {
        this.slotName = 'split';
    }
}
FadvSplitButtonDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSplitButtonDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvSplitButtonDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvSplitButtonDirective, selector: "[fadvSplitButtonMenu]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSplitButtonDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fadvSplitButtonMenu]',
                }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

class FadvSummaryIconDirective {
    constructor() {
        this.slotName = 'summary-icon';
    }
}
FadvSummaryIconDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSummaryIconDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvSummaryIconDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvSummaryIconDirective, selector: "[fadvSummaryIcon]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvSummaryIconDirective, decorators: [{
            type: Directive,
            args: [{ selector: '[fadvSummaryIcon]' }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

class FadvStepLabelDirective {
    constructor() {
        this.slotName = 'step-label';
    }
}
FadvStepLabelDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvStepLabelDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive });
FadvStepLabelDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FadvStepLabelDirective, selector: "[fadvStepLabel]", host: { properties: { "slot": "this.slotName" } }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvStepLabelDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fadvStepLabel]',
                }]
        }], propDecorators: { slotName: [{
                type: HostBinding,
                args: ['slot']
            }] } });

const blurEventOutput = 'blurred';
function mixinBlurEvent(Base) {
    class BlurEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.blurred = new EventEmitter();
        }
        _forwardBlurEvent(event) {
            event.stopPropagation();
            this.blurred.emit(event);
        }
        blur() {
            var _a, _b;
            (_b = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.blur();
        }
    }
    BlurEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: BlurEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    BlurEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: BlurEventForwarder, outputs: { blurred: "blurred" }, host: { listeners: { "sl-blur": "_forwardBlurEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], BlurEventForwarder.prototype, "blurred", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: BlurEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { blurred: [{
                    type: Output
                }], _forwardBlurEvent: [{
                    type: HostListener,
                    args: ['sl-blur', ['$event']]
                }] } });
    return BlurEventForwarder;
}

const changeEventOutput = 'changed';
function mixinChangeEvent(Base) {
    class ChangeEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.changed = new EventEmitter();
        }
        _forwardChangeEvent(event) {
            event.stopPropagation();
            this.changed.emit(event);
        }
    }
    ChangeEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ChangeEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    ChangeEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: ChangeEventForwarder, outputs: { changed: "changed" }, host: { listeners: { "sl-change": "_forwardChangeEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], ChangeEventForwarder.prototype, "changed", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ChangeEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { changed: [{
                    type: Output
                }], _forwardChangeEvent: [{
                    type: HostListener,
                    args: ['sl-change', ['$event']]
                }] } });
    return ChangeEventForwarder;
}

const clearEventOutput = 'cleared';
function mixinClearEvent(Base) {
    class ClearEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.cleared = new EventEmitter();
        }
        _forwardClearEvent(event) {
            event.stopPropagation();
            this.cleared.emit(event);
        }
    }
    ClearEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ClearEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    ClearEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: ClearEventForwarder, outputs: { cleared: "cleared" }, host: { listeners: { "sl-clear": "_forwardClearEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], ClearEventForwarder.prototype, "cleared", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ClearEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { cleared: [{
                    type: Output
                }], _forwardClearEvent: [{
                    type: HostListener,
                    args: ['sl-clear', ['$event']]
                }] } });
    return ClearEventForwarder;
}

function mixinClickEvent(Base) {
    class ClickEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.clicked = new EventEmitter();
        }
        _forwardClickEvent(event) {
            this.clicked.emit(event);
        }
        click() {
            var _a, _b;
            (_b = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.click();
        }
    }
    ClickEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ClickEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    ClickEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: ClickEventForwarder, outputs: { clicked: "clicked" }, host: { listeners: { "click": "_forwardClickEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], ClickEventForwarder.prototype, "clicked", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ClickEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { clicked: [{
                    type: Output
                }], _forwardClickEvent: [{
                    type: HostListener,
                    args: ['click', ['$event']]
                }] } });
    return ClickEventForwarder;
}

const focusEventOutput = 'focused';
function mixinFocusEvent(Base) {
    class FocusEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.focused = new EventEmitter();
        }
        _forwardFocusEvent(event) {
            event.stopPropagation();
            this.focused.emit(event);
        }
        focus(options) {
            this.elementRef.nativeElement.focus(options);
        }
    }
    FocusEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FocusEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    FocusEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FocusEventForwarder, outputs: { focused: "focused" }, host: { listeners: { "sl-focus": "_forwardFocusEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], FocusEventForwarder.prototype, "focused", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FocusEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { focused: [{
                    type: Output
                }], _forwardFocusEvent: [{
                    type: HostListener,
                    args: ['sl-focus', ['$event']]
                }] } });
    return FocusEventForwarder;
}

const formControlDisabledInput = 'disabled';
const formControlValueInput = 'value';
const formControlValueOutput = 'valueChange';
function mixinFormControl(Base) {
    // MD - I looked at this a couple months later and it took me a second to remember what all this was for.
    // The "value" of a component can change in the following ways
    // 1. The web component value changes
    // 2. The wrapper property is changed
    // 3. The form group value for the property is changed
    // This mixin provides everything that is needed to keep everything in sync in one place.
    class FormControl extends Base {
        constructor() {
            super(...arguments);
            this.disabled = false;
            this.valueChange = new EventEmitter();
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            this.onChange = (_) => { };
            // eslint-disable-next-line @typescript-eslint/no-empty-function
            this.onTouched = () => { };
        }
        // Comes from ControlValueAccess, this is what the form uses to write values to the wrapper component
        writeValue(obj) {
            if (!this.changes) {
                this.changes = new BehaviorSubject(obj);
            }
            else {
                this.changes.next(obj);
            }
        }
        // Comes from ControlValueAccessor, passes in a function that the wrapper component uses can emit changes from the wrapper to the form
        registerOnChange(fn) {
            this.onChange = fn;
        }
        reportChange(value) {
            // emit the value change to support template forms
            this.valueChange.emit(value);
            // invoke on change to support reactive forms
            this.onChange(value);
        }
        // Comes from ControlValueAccessor, passes in a function that the wrapper component uses to alert the form if the input has been
        // touched by the user. Needed because the form won't run the validation for a control unless it has been touched.
        registerOnTouched(fn) {
            this.onTouched = fn;
        }
        // This allows the wrapper component to pass in a function that will update the web component when the form value changes.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        registerFormValueChangeHandler(handler) {
            var _a;
            (_a = this.changes) === null || _a === void 0 ? void 0 : _a.subscribe(handler);
        }
        setDisabledState(isDisabled) {
            this.disabled = isDisabled;
        }
    }
    FormControl.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FormControl, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    FormControl.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: FormControl, inputs: { value: "value", disabled: "disabled" }, outputs: { valueChange: "valueChange" }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Forward()
    ], FormControl.prototype, "value", void 0);
    __decorate([
        Forward()
    ], FormControl.prototype, "disabled", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FormControl, decorators: [{
                type: Directive
            }], propDecorators: { value: [{
                    type: Input
                }], disabled: [{
                    type: Input
                }], valueChange: [{
                    type: Output
                }] } });
    return FormControl;
}
function createFormControlProvider(control) {
    return {
        provide: NG_VALUE_ACCESSOR,
        useExisting: control,
        multi: true,
    };
}

const hideEventOutput = 'hidden';
function mixinHideEvent(Base) {
    class HideEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.hidden = new EventEmitter();
        }
        _forwardHideEvent(event) {
            event.stopPropagation();
            this.hidden.emit(event);
        }
        hide() {
            var _a, _b;
            (_b = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.hide();
        }
    }
    HideEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: HideEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    HideEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: HideEventForwarder, outputs: { hidden: "hidden" }, host: { listeners: { "sl-after-hide": "_forwardHideEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], HideEventForwarder.prototype, "hidden", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: HideEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { hidden: [{
                    type: Output
                }], _forwardHideEvent: [{
                    type: HostListener,
                    args: ['sl-after-hide', ['$event']]
                }] } });
    return HideEventForwarder;
}

const inputEventOutput = 'inputChanged';
function mixinInputEvent(Base) {
    class InputEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.inputChanged = new EventEmitter();
        }
        _forwardInputEvent(event) {
            event.stopPropagation();
            this.inputChanged.emit(event);
        }
    }
    InputEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: InputEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    InputEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: InputEventForwarder, outputs: { inputChanged: "inputChanged" }, host: { listeners: { "sl-input": "_forwardInputEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: InputEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { inputChanged: [{
                    type: Output
                }], _forwardInputEvent: [{
                    type: HostListener,
                    args: ['sl-input', ['$event']]
                }] } });
    return InputEventForwarder;
}

const showEventOutput = 'shown';
function mixinShowEvent(Base) {
    class ShowEventForwarder extends Base {
        constructor() {
            super(...arguments);
            this.shown = new EventEmitter();
        }
        _forwardShowEvent(event) {
            event.stopPropagation();
            this.shown.emit(event);
        }
        show() {
            var _a, _b;
            (_b = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement) === null || _b === void 0 ? void 0 : _b.show();
        }
    }
    ShowEventForwarder.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ShowEventForwarder, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    ShowEventForwarder.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: ShowEventForwarder, outputs: { shown: "shown" }, host: { listeners: { "sl-after-show": "_forwardShowEvent($event)" } }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Track()
    ], ShowEventForwarder.prototype, "shown", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: ShowEventForwarder, decorators: [{
                type: Directive
            }], propDecorators: { shown: [{
                    type: Output
                }], _forwardShowEvent: [{
                    type: HostListener,
                    args: ['sl-after-show', ['$event']]
                }] } });
    return ShowEventForwarder;
}

const openChangeInput = 'open';
const openChangeChangeOutput = 'openChange';
const openChangeHiddenOutput = 'hidden';
const openChangeShownOutput = 'shown';
/**
 * Mixes in the hide and show events, and adds the open Input() and openChanged Output and
 * wires up the events to support the open change functionality
 * @param Base
 * @returns
 */
function mixinOpenChange(Base) {
    class OpenChange extends mixinShowEvent(mixinHideEvent(Base)) {
        constructor() {
            super(...arguments);
            this.open = false;
            this.openChange = new EventEmitter();
        }
        ngOnInit() {
            super.ngOnInit();
            this.registerSubscription(this.shown, () => this._emitOpenChange(true));
            this.registerSubscription(this.hidden, () => this._emitOpenChange(false));
        }
        _emitOpenChange(state) {
            this.openChange.emit(state);
        }
    }
    OpenChange.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: OpenChange, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    OpenChange.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: OpenChange, inputs: { open: "open" }, outputs: { openChange: "openChange" }, usesInheritance: true, ngImport: i0 });
    __decorate([
        Forward()
    ], OpenChange.prototype, "open", void 0);
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: OpenChange, decorators: [{
                type: Directive
            }], propDecorators: { open: [{
                    type: Input
                }], openChange: [{
                    type: Output
                }] } });
    return OpenChange;
}

const styleMap = {
    small: '--sl-font-size-small',
    medium: '--sl-font-size-regular',
    large: '--sl-font-size-large',
};
const sizingInput = 'size';
function mixinSizing(Base) {
    class SizedComponent extends Base {
        constructor() {
            super(...arguments);
            this.size = 'medium';
        }
        // eslint-disable-next-line @angular-eslint/use-lifecycle-interface
        ngOnChanges(changes) {
            var _a;
            super.ngOnChanges(changes);
            const el = (_a = this.elementRef) === null || _a === void 0 ? void 0 : _a.nativeElement;
            if (el) {
                el.setAttribute('style', `--symbol-size: var(${styleMap[this.size] || styleMap['medium']}); font-size: var(--symbol-size);`);
            }
        }
    }
    SizedComponent.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: SizedComponent, deps: null, target: i0.ɵɵFactoryTarget.Directive });
    SizedComponent.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "14.2.3", type: SizedComponent, inputs: { size: "size" }, usesInheritance: true, usesOnChanges: true, ngImport: i0 });
    i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: SizedComponent, decorators: [{
                type: Directive
            }], propDecorators: { size: [{
                    type: Input
                }] } });
    return SizedComponent;
}

class FadvCommonModule {
}
FadvCommonModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCommonModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FadvCommonModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "14.2.3", ngImport: i0, type: FadvCommonModule, declarations: [FadvAppendChildDirective,
        FadvLetDirective,
        FadvBadgeDirective,
        FadvPrefixDirective,
        FadvSuffixDirective,
        FadvSplitButtonDirective,
        FadvAutofocusDirective,
        FadvSummaryIconDirective,
        FadvStepLabelDirective], imports: [I18NextModule, CommonModule], exports: [I18NextModule,
        CommonModule,
        FadvAppendChildDirective,
        FadvLetDirective,
        FadvBadgeDirective,
        FadvPrefixDirective,
        FadvSuffixDirective,
        FadvSplitButtonDirective,
        FadvAutofocusDirective,
        FadvSummaryIconDirective,
        FadvStepLabelDirective] });
FadvCommonModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCommonModule, providers: [FadvComponentsAnalyticsService], imports: [I18NextModule, CommonModule, I18NextModule,
        CommonModule] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "14.2.3", ngImport: i0, type: FadvCommonModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FadvAppendChildDirective,
                        FadvLetDirective,
                        FadvBadgeDirective,
                        FadvPrefixDirective,
                        FadvSuffixDirective,
                        FadvSplitButtonDirective,
                        FadvAutofocusDirective,
                        FadvSummaryIconDirective,
                        FadvStepLabelDirective,
                    ],
                    imports: [I18NextModule, CommonModule],
                    exports: [
                        I18NextModule,
                        CommonModule,
                        FadvAppendChildDirective,
                        FadvLetDirective,
                        FadvBadgeDirective,
                        FadvPrefixDirective,
                        FadvSuffixDirective,
                        FadvSplitButtonDirective,
                        FadvAutofocusDirective,
                        FadvSummaryIconDirective,
                        FadvStepLabelDirective,
                    ],
                    providers: [FadvComponentsAnalyticsService],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { FadvAppendChildDirective, FadvAutofocusDirective, FadvBadgeDirective, FadvBaseComponent, FadvCommonModule, FadvComponentsAnalyticsService, FadvIconLibraryService, FadvLetDirective, FadvNestedHtmlDirective, FadvPrefixDirective, FadvSplitButtonDirective, FadvStepLabelDirective, FadvSuffixDirective, FadvSummaryIconDirective, Forward, Track, blurEventOutput, changeEventOutput, clearEventOutput, createFormControlProvider, focusEventOutput, formControlDisabledInput, formControlValueInput, formControlValueOutput, hideEventOutput, inputEventOutput, mixinBlurEvent, mixinChangeEvent, mixinClearEvent, mixinClickEvent, mixinFocusEvent, mixinFormControl, mixinHideEvent, mixinInputEvent, mixinOpenChange, mixinShowEvent, mixinSizing, openChangeChangeOutput, openChangeHiddenOutput, openChangeInput, openChangeShownOutput, showEventOutput, sizingInput };
